import React, { useState } from 'react';
import { Sparkles, User, Building, TrendingUp, Loader, Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface PersonalizationData {
  firstName?: string;
  lastName?: string;
  company?: string;
  role?: string;
  linkedin_headline?: string;
  linkedin_recent_activity?: string;
  company_recent_news?: string[];
  recent_funding?: string;
  company_tech_stack?: string[];
}

interface PersonalizationAssistantProps {
  onUseContent?: (subject: string, body: string) => void;
}

const PersonalizationAssistant: React.FC<PersonalizationAssistantProps> = ({
  onUseContent
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [step, setStep] = useState<'input' | 'result'>('input');
  
  const [formData, setFormData] = useState<PersonalizationData>({
    firstName: '',
    lastName: '',
    company: '',
    role: '',
    linkedin_headline: '',
    linkedin_recent_activity: '',
    company_recent_news: [],
    recent_funding: '',
    company_tech_stack: []
  });

  const [result, setResult] = useState<any>(null);

  const handleGenerate = async () => {
    if (!formData.firstName || !formData.company) {
      toast.error('First name and company are required');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch('/api/email/personalization/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          template: 'Generate highly personalized outreach email',
          data: formData,
          purpose: 'Initial outreach'
        })
      });

      const data = await response.json();
      if (data.success) {
        setResult(data.data);
        setStep('result');
        toast.success('Personalized content generated!');
      }
    } catch (error: any) {
      console.error('Generation error:', error);
      toast.error('Failed to generate content');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700"
      >
        <Sparkles className="w-4 h-4" />
        Personalization Assistant
      </button>
    );
  }

  return (
    <div className="border rounded-lg bg-white p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-purple-600" />
          <h3 className="text-lg font-semibold">Personalization Assistant</h3>
        </div>
        <button
          onClick={() => {
            setIsOpen(false);
            setStep('input');
            setResult(null);
          }}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      {step === 'input' ? (
        // Input Form
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="John"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="Doe"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Acme Corp"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role/Title
            </label>
            <input
              type="text"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              placeholder="Head of Marketing"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              LinkedIn Headline
            </label>
            <input
              type="text"
              value={formData.linkedin_headline}
              onChange={(e) => setFormData({ ...formData, linkedin_headline: e.target.value })}
              placeholder="Marketing Director at Acme Corp | Growth Enthusiast"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recent LinkedIn Activity
            </label>
            <textarea
              value={formData.linkedin_recent_activity}
              onChange={(e) => setFormData({ ...formData, linkedin_recent_activity: e.target.value })}
              placeholder="Posted about scaling challenges with marketing attribution..."
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recent Company News or Funding
            </label>
            <input
              type="text"
              value={formData.recent_funding}
              onChange={(e) => setFormData({ ...formData, recent_funding: e.target.value })}
              placeholder="Raised $10M Series A led by Sequoia"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <button
            onClick={handleGenerate}
            disabled={isGenerating || !formData.firstName || !formData.company}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isGenerating ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Personalized Email
              </>
            )}
          </button>
        </div>
      ) : (
        // Results
        <div className="space-y-6">
          {/* Personalization Score */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Personalization Score</p>
                <p className="text-3xl font-bold text-purple-600">{result.personalization_score}/100</p>
              </div>
              <TrendingUp className="w-12 h-12 text-purple-400" />
            </div>
            {result.used_data_points.length > 0 && (
              <p className="text-xs text-gray-600 mt-2">
                Used: {result.used_data_points.join(', ')}
              </p>
            )}
          </div>

          {/* Subject Variations */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject Line Options:</label>
            <div className="space-y-2">
              {result.subject_variations.map((subject: string, i: number) => (
                <div key={i} className="flex items-center gap-2 p-3 bg-gray-50 rounded border hover:border-purple-300 cursor-pointer group">
                  <span className="flex-1 text-sm">{subject}</span>
                  <button
                    onClick={() => copyToClipboard(subject)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Opening Lines */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Opening Line Options:</label>
            <div className="space-y-2">
              {result.opening_lines.map((line: string, i: number) => (
                <div key={i} className="flex items-center gap-2 p-3 bg-gray-50 rounded border hover:border-purple-300 cursor-pointer group">
                  <span className="flex-1 text-sm">{line}</span>
                  <button
                    onClick={() => copyToClipboard(line)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Full Email Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Complete Email:</label>
            <div className="relative">
              <div className="bg-gray-50 rounded border p-4 whitespace-pre-wrap text-sm max-h-64 overflow-y-auto">
                {result.body_content}
              </div>
              <button
                onClick={() => copyToClipboard(result.body_content)}
                className="absolute top-2 right-2 p-2 bg-white rounded border hover:bg-gray-50"
              >
                <Copy className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                if (onUseContent && result.subject_variations.length > 0) {
                  onUseContent(result.subject_variations[0], result.body_content);
                  toast.success('Content applied to campaign!');
                  setIsOpen(false);
                }
              }}
              className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700"
            >
              Use This Content
            </button>
            <button
              onClick={() => setStep('input')}
              className="px-6 py-3 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
            >
              Generate New
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalizationAssistant;
